import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Row, 
  Col, 
  Input, 
  Select, 
  Button, 
  Space, 
  Typography, 
  Tag 
} from 'antd';
import { 
  SearchOutlined, 
  ClearOutlined, 
  FilterOutlined, 
  ReloadOutlined,
  UpOutlined,
  DownOutlined,
  ShopOutlined,
  TagOutlined,
  CloseCircleOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { 
  FILTER_TYPES, 
  DEFAULT_FILTER_VALUES, 
  FilterManager 
} from '../constants/filterTypes';
import SearchInput from './SearchInput';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const ListingsFilter = ({
  onFilterChange,
  onClearFilters,
  onApplyFilters,
  onReload,
  filters,
  loading
}) => {
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  
  // Extract filter values for easier access with defaults
  const {
    searchText = DEFAULT_FILTER_VALUES[FILTER_TYPES.SEARCH],
    selectedFulfillmentChannel = DEFAULT_FILTER_VALUES[FILTER_TYPES.FULFILLMENT_CHANNEL],
    selectedStatus = DEFAULT_FILTER_VALUES[FILTER_TYPES.STATUS],
    selectedSellerAccount = DEFAULT_FILTER_VALUES[FILTER_TYPES.SELLER_ACCOUNT]
  } = filters;

  const [localFilters, setLocalFilters] = useState({
    searchText,
    selectedFulfillmentChannel,
    selectedStatus,
    selectedSellerAccount: Array.isArray(selectedSellerAccount) ? selectedSellerAccount : (selectedSellerAccount === 'all' ? [] : selectedSellerAccount ? [selectedSellerAccount] : [])
  });

  const { sellerAccounts } = useSelector(state => state.dataCommon);

  // Filter type mapping constant to avoid duplication
  const FILTER_TYPE_MAP = {
    'searchText': FILTER_TYPES.SEARCH,
    'selectedFulfillmentChannel': FILTER_TYPES.FULFILLMENT_CHANNEL,
    'selectedStatus': FILTER_TYPES.STATUS,
    'selectedSellerAccount': FILTER_TYPES.SELLER_ACCOUNT
  };

  // Sync local filters with props when they change externally
  useEffect(() => {
    setLocalFilters({
      searchText,
      selectedFulfillmentChannel,
      selectedStatus,
      selectedSellerAccount: Array.isArray(selectedSellerAccount) ? selectedSellerAccount : (selectedSellerAccount === 'all' ? [] : selectedSellerAccount ? [selectedSellerAccount] : [])
    });
  }, [searchText, selectedFulfillmentChannel, selectedStatus, selectedSellerAccount]);

  // Handle individual filter changes (only update local state)
  const handleLocalFilterChange = useCallback((filterKey, value) => {
    setLocalFilters(prev => ({ ...prev, [filterKey]: value }));
  }, []);

  // Get search text in comma-separated format for API calls
  const getSearchTextForAPI = useCallback((text) => {
    if (!text) return '';
    
    // Convert to comma-separated format and clean up
    const searchTerms = text.includes('\n') 
      ? text.split('\n').map(item => item.trim()).filter(item => item.length > 0)
      : text.split(',').map(item => item.trim()).filter(item => item.length > 0);
    
    return searchTerms.join(',');
  }, []);

  // Handle search input change (only update local state)
  const handleSearchInputChange = useCallback((value) => {
    handleLocalFilterChange('searchText', value);
  }, [handleLocalFilterChange]);

  // Handle search execution
  const handleSearchExecute = useCallback((searchValue) => {
    const apiSearchText = getSearchTextForAPI(searchValue || localFilters.searchText);
    onFilterChange(FILTER_TYPES.SEARCH, apiSearchText);
    onApplyFilters();
  }, [onFilterChange, onApplyFilters, getSearchTextForAPI, localFilters.searchText]);

  // Handle clear search with reload
  const handleClearSearch = useCallback(() => {
    handleLocalFilterChange('searchText', '');
    onFilterChange(FILTER_TYPES.SEARCH, '');
    onApplyFilters();
    onReload({ preserveSelection: false });
  }, [handleLocalFilterChange, onFilterChange, onApplyFilters, onReload]);

  const handleClearFilters = useCallback(() => {
    const defaultFilters = FilterManager.getDefaultFilters();
    setLocalFilters({
      ...defaultFilters,
      selectedSellerAccount: [] // Convert to array for multi-select
    });
    onClearFilters();
  }, [onClearFilters]);

  const handleApplyFilters = useCallback(() => {
    // Apply all local filters using the generic handler with constants
    const filterUpdates = [
      { type: FILTER_TYPES.SEARCH, value: localFilters.searchText },
      { type: FILTER_TYPES.FULFILLMENT_CHANNEL, value: localFilters.selectedFulfillmentChannel },
      { type: FILTER_TYPES.STATUS, value: localFilters.selectedStatus },
      { type: FILTER_TYPES.SELLER_ACCOUNT, value: localFilters.selectedSellerAccount }
    ];

    // Apply all filter updates
    filterUpdates.forEach(({ type, value }) => {
      onFilterChange(type, value);
    });

    // Execute the actual filter application
    onApplyFilters();
    setIsFilterExpanded(false);
  }, [localFilters, onFilterChange, onApplyFilters]);

  // Individual clear functions for specific filters using constants
  const clearIndividualFilter = useCallback((filterKey) => {
    const defaultValue = DEFAULT_FILTER_VALUES[FILTER_TYPE_MAP[filterKey]];
    if (filterKey === 'selectedSellerAccount') {
      handleLocalFilterChange(filterKey, []);
    } else {
      handleLocalFilterChange(filterKey, defaultValue);
    }
  }, [handleLocalFilterChange, FILTER_TYPE_MAP]);

  // Handle individual filter tag removal and apply filters immediately
  const handleRemoveFilterTag = useCallback((filterKey) => {
    const filterType = FILTER_TYPE_MAP[filterKey];
    if (!filterType) return;

    let defaultValue = DEFAULT_FILTER_VALUES[filterType];
    if (filterKey === 'selectedSellerAccount') {
      defaultValue = [];
    }

    // Update local state
    setLocalFilters(prev => ({ ...prev, [filterKey]: defaultValue }));
    
    // Apply filter change immediately
    onFilterChange(filterType, defaultValue);
    onApplyFilters();
  }, [onFilterChange, onApplyFilters, FILTER_TYPE_MAP]);

  // Check if local filters have changes compared to applied filters using utility
  const hasUnappliedChanges = useMemo(() => {
    return FilterManager.hasChanges(localFilters, filters);
  }, [localFilters, filters]);

  // Memoize active filters count based on applied filters using utility
  const activeFiltersCount = useMemo(() => {
    return FilterManager.getActiveFiltersCount(filters);
  }, [filters]);

  // Memoize filter summary to prevent unnecessary re-renders
  const renderFilterSummary = useMemo(() => {
    const tags = [];

    if (searchText && searchText.trim()) {
      tags.push(
        <Tag 
          key="search" 
          closable 
          color="blue"
          onClose={() => handleRemoveFilterTag('searchText')}
        >
          Search: {searchText.length > 20 ? `${searchText.substring(0, 20)}...` : searchText}
        </Tag>
      );
    }

    if (selectedFulfillmentChannel && selectedFulfillmentChannel !== 'all') {
      const channelText = selectedFulfillmentChannel === 'FBA' ? 'FBA' : 
                         selectedFulfillmentChannel === 'FBM' ? 'FBM' : selectedFulfillmentChannel;
      tags.push(
        <Tag 
          key="fulfillmentChannel" 
          closable 
          color="orange"
          onClose={() => handleRemoveFilterTag('selectedFulfillmentChannel')}
        >
          Channel: {channelText}
        </Tag>
      );
    }

    if (selectedStatus && selectedStatus !== 'all') {
      tags.push(
        <Tag 
          key="status" 
          closable 
          color="green"
          onClose={() => handleRemoveFilterTag('selectedStatus')}
        >
          Status: {selectedStatus}
        </Tag>
      );
    }

    if (selectedSellerAccount && Array.isArray(selectedSellerAccount) && selectedSellerAccount.length > 0) {
      const accountNames = selectedSellerAccount.map(id => {
        const account = sellerAccounts?.find(acc => acc.id === id);
        return account ? account.name : id;
      });
      
      tags.push(
        <Tag 
          key="sellerAccount" 
          closable 
          color="purple"
          onClose={() => handleRemoveFilterTag('selectedSellerAccount')}
        >
          Accounts: {accountNames.length > 2 ? `${accountNames.slice(0, 2).join(', ')}... (+${accountNames.length - 2})` : accountNames.join(', ')}
        </Tag>
      );
    }

    return tags;
  }, [searchText, selectedFulfillmentChannel, selectedStatus, selectedSellerAccount, sellerAccounts, handleRemoveFilterTag]);

  return (
    <div style={{ paddingBottom: 10, borderBottom: '1px solid #f0f0f0' }}>
      <Row gutter={16} align="middle">
        <Col flex="auto">
          <Row>
            <Col span={6}>
              <SearchInput
                value={localFilters.searchText}
                onChange={(value) => handleLocalFilterChange('searchText', value)}
                onSearch={handleSearchExecute}
                onClear={handleClearSearch}
                placeholder="Search by SKU, ASIN or product name (use commas for multiple SKUs/ASINs)"
                textareaPlaceholder="Search by SKU, ASIN or product name (one per line or use commas)"
              />
              {/* Show search hint for multiple values */}
              {localFilters.searchText && (localFilters.searchText.includes(',') || localFilters.searchText.includes('\n')) && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#1890ff', 
                  marginTop: '4px',
                  fontStyle: 'italic'
                }}>
                  🔍 Searching for {getSearchTextForAPI(localFilters.searchText).split(',').filter(val => val.trim().length > 0).length} items (SKUs/ASINs only)
                </div>
              )}
              {/* Show hint when user has unsaved changes */}
              {hasUnappliedChanges && (
                <Text
                  type="secondary"
                  style={{
                    fontSize: 11,
                    fontStyle: 'italic',
                    color: '#fa8c16'
                  }}
                >
                  You have unsaved filter changes. Click "Apply Filters" to search.
                </Text>
              )}
            </Col>
          </Row>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => onReload({ preserveSelection: true })}
              loading={loading}
              title="Reload with current filters"
            >
              Reload
            </Button>
            <Button
              type={isFilterExpanded ? "primary" : "default"}
              icon={<FilterOutlined />}
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            >
              Filters
              {activeFiltersCount > 0 && (
                <span style={{
                  marginLeft: 4,
                  backgroundColor: '#ff4d4f',
                  color: 'white',
                  borderRadius: '50%',
                  width: 18,
                  height: 18,
                  display: 'inline-block',
                  textAlign: 'center',
                  fontSize: 12,
                  lineHeight: '18px'
                }}>
                  {activeFiltersCount}
                </span>
              )}
              {hasUnappliedChanges && (
                <span style={{
                  marginLeft: 4,
                  backgroundColor: '#fa8c16',
                  color: 'white',
                  borderRadius: '50%',
                  width: 6,
                  height: 6,
                  display: 'inline-block'
                }} title="You have unsaved changes">
                </span>
              )}
              {isFilterExpanded ? <UpOutlined style={{ marginLeft: 4 }} /> : <DownOutlined style={{ marginLeft: 4 }} />}
            </Button>
            {activeFiltersCount > 0 && (
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearFilters}
                title="Clear all filters"
              >
                Clear Filters
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* Active filters summary */}
      {activeFiltersCount > 0 && !isFilterExpanded && (
        <Row style={{ marginTop: 12 }}>
          <Col span={24}>
            <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
              Active filters:
            </Text>
            <Space size={4} wrap>
              {renderFilterSummary}
            </Space>
          </Col>
        </Row>
      )}

      {/* Expanded filters */}
      {isFilterExpanded && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #f0f0f0' }}>
          <Row gutter={[16, 16]}>
            {/* Fulfillment Channel */}
            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedFulfillmentChannel !== 'all' ? '#1890ff' : undefined }}>
                  <ShopOutlined style={{ marginRight: 4 }} />
                  Fulfillment Channel
                </Text>
                {localFilters.selectedFulfillmentChannel !== 'all' && (
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<CloseCircleOutlined />} 
                    onClick={() => clearIndividualFilter('selectedFulfillmentChannel')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear fulfillment channel filter"
                  />
                )}
              </div>
              <Select
                style={{ width: '100%' }}
                onChange={(value) => handleLocalFilterChange('selectedFulfillmentChannel', value)}
                value={localFilters.selectedFulfillmentChannel}
                placeholder="Select fulfillment channel"
              >
                <Option value="all">All Channels</Option>
                <Option value="FBA">FBA</Option>
                <Option value="FBM">FBM</Option>
              </Select>
            </Col>

            {/* Status */}
            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedStatus !== 'all' ? '#1890ff' : undefined }}>
                  <TagOutlined style={{ marginRight: 4 }} />
                  Status
                </Text>
                {localFilters.selectedStatus !== 'all' && (
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<CloseCircleOutlined />} 
                    onClick={() => clearIndividualFilter('selectedStatus')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear status filter"
                  />
                )}
              </div>
              <Select
                style={{ width: '100%' }}
                onChange={(value) => handleLocalFilterChange('selectedStatus', value)}
                value={localFilters.selectedStatus}
                placeholder="Select status"
              >
                <Option value="all">All Status</Option>
                <Option value="ACTIVE">Active</Option>
                <Option value="INACTIVE">Inactive</Option>
                <Option value="DELETED">Deleted</Option>
              </Select>
            </Col>

            {/* Seller Account */}
            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: (localFilters.selectedSellerAccount && Array.isArray(localFilters.selectedSellerAccount) && localFilters.selectedSellerAccount.length > 0) ? '#1890ff' : undefined }}>
                  <ShopOutlined style={{ marginRight: 4 }} />
                  Seller Account
                </Text>
                {(localFilters.selectedSellerAccount && Array.isArray(localFilters.selectedSellerAccount) && localFilters.selectedSellerAccount.length > 0) && (
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<CloseCircleOutlined />} 
                    onClick={() => clearIndividualFilter('selectedSellerAccount')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear seller account filter"
                  />
                )}
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                onChange={(value) => handleLocalFilterChange('selectedSellerAccount', value)}
                value={localFilters.selectedSellerAccount}
                placeholder="Select accounts (multiple allowed)"
                allowClear
                maxTagCount="responsive"
                loading={!sellerAccounts}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {sellerAccounts && sellerAccounts.map(account => (
                  <Option key={account.id} value={account.id}>
                    {account.name}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          {/* Apply buttons */}
          <Row justify="end" style={{ marginTop: 16 }}>
            <Col>
              <Space>
                <Button onClick={handleClearFilters}>
                  Clear All
                </Button>
                <Button
                  type={hasUnappliedChanges ? "primary" : "default"}
                  onClick={handleApplyFilters}
                  icon={hasUnappliedChanges ? <CheckOutlined /> : <FilterOutlined />}
                >
                  {hasUnappliedChanges ? 'Apply Changes' : 'Apply Filters'}
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default ListingsFilter;